class PostalCodeBadge extends HTMLElement {
  constructor() {
    super();

    this.toggleButton = this.querySelector('[data-postal-toggle]');
    this.dialog = this.querySelector('[data-postal-dialog]');
    this.form = this.querySelector('[data-postal-form]');
    this.input = this.querySelector('[data-postal-input]');
    this.value = this.querySelector('[data-postal-value]');
    this.valueDialog = this.querySelector('[data-postal-value-dialog]');
    this.action = this.querySelector('[data-postal-action]');
    this.closeButton = this.querySelector('[data-postal-close]');

    if (!this.toggleButton || !this.dialog || !this.form || !this.input || !this.value || !this.valueDialog || !this.action || !this.closeButton) {
      return;
    }

    this.defaultPostalCode = this.dataset.defaultPostal || 'H0H0H0';
    this.currentPostalCode = localStorage.getItem('postalCode') || '';

    this.overlay = null;
    this.isDialogOpen = false;
    this.mobileBreakpoint = 1049;
    this.isMobileLayout = false;
    this.originalParent = this.parentElement;
    this.originalNextSibling = this.nextSibling;
    this.headerWrapper = this.closest('.header-wrapper');
    this.headerElement = this.closest('header');
    this.isResizeListenerActive = false;

    this.onToggle = this.onToggle.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onCloseButtonClick = this.onCloseButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onResize = this.onResize.bind(this);

    this.renderDisplayValue();
    this.input.value = this.currentPostalCode;

    this.toggleButton.addEventListener('click', this.onToggle);
    this.form.addEventListener('submit', this.onSubmit);
    this.closeButton.addEventListener('click', this.onCloseButtonClick);

    this.updateResponsiveLayout();
    this.addResizeListener();
  }

  connectedCallback() {
    if (!this.toggleButton) {
      return;
    }

    this.updateResponsiveLayout();
    this.addResizeListener();
  }

  disconnectedCallback() {
    this.isResizeListenerActive = false;
    window.removeEventListener('resize', this.onResize);
  }

  addResizeListener() {
    if (this.isResizeListenerActive) {
      return;
    }

    window.addEventListener('resize', this.onResize);
    this.isResizeListenerActive = true;
  }

  onToggle(event) {
    if (this.isMobileLayout && !event.target.closest('[data-postal-action]')) {
      return;
    }

    if (this.isDialogOpen) {
      this.close();
      return;
    }

    this.open();
  }

  async onSubmit(event) {
    event.preventDefault();
    const nextValue = this.input.value.trim();
    const normalizedValue = this.getNormalizedPostalCode(nextValue);
    const confirmButton = this.form.querySelector('.postal-code-badge__confirm');
    const spinner = this.form.querySelector('.loading-overlay__spinner');
    const confirmText = this.form.querySelector('.postal-code-badge__confirm-text');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
    if (confirmText) {
      confirmText.classList.add('hidden');
    }

    if (normalizedValue.length <= 0) {
      this.close();
      return;
    }

    if (confirmButton) {
      confirmButton.disabled = true;
    }
    this.form.setAttribute('aria-busy', 'true');

    try {
      window.postalCodeLocation = 'header';
      const validationResult = await this.validatePostalCode(normalizedValue);
      const redirectUrl = this.getFranchiseRedirectUrl(validationResult, normalizedValue);

      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      this.currentPostalCode = normalizedValue;
      localStorage.setItem('postalCode', this.currentPostalCode);
      this.renderDisplayValue();
      location.reload();
    } finally {
      this.form.removeAttribute('aria-busy');
      if (confirmButton) {
        confirmButton.disabled = false;
      }
      if (spinner) {
        spinner.classList.add('hidden');
      }
      if (confirmText) {
        confirmText.classList.remove('hidden');
      }
    }
  }

  renderDisplayValue() {
    const rawValue = this.currentPostalCode || this.defaultPostalCode;
    const displayValue = this.formatPostalCodeForDisplay(rawValue);
    this.value.textContent = displayValue;
    this.valueDialog.textContent = displayValue;
  }

  getNormalizedPostalCode(postalCode) {
    if (!postalCode) {
      return '';
    }

    if (this.bosapinUtils?.normalizePostalCode) {
      return this.bosapinUtils.normalizePostalCode(postalCode);
    }

    return postalCode.toString().trim().toUpperCase().replace(/\s+/g, '');
  }

  async validatePostalCode(postalCode) {
    if (!this.bosapinUtils) {
      this.bosapinUtils = document.querySelector('bosapin-utils');
    }

    if (!this.bosapinUtils?.validatePostalCode) {
      return null;
    }

    try {
      return await this.bosapinUtils.validatePostalCode(postalCode, true);
    } catch (error) {
      console.warn('Postal code validation failed', error);
      return null;
    }
  }

  formatPostalCodeForDisplay(postal) {
    if (!postal) return '';
    const str = postal.toString().replace(/\s+/g, '').toUpperCase();
    if (str.length <= 3) return str;
    return `${str.slice(0, 3)} ${str.slice(3)}`;
  }

  getFranchiseRedirectUrl(validationResult, normalizedPostalCode) {
    if (!validationResult || validationResult.message !== 'postal_code_found_in_another_franchise') {
      return null;
    }

    const rawWebsite = validationResult?.data?.website || validationResult.website;
    if (!rawWebsite) {
      return null;
    }

    const trustedDomains = ['bosapin.com', 'treasytrees.ca', 'treasytrees.com', 'myshopify.com'];
    const isAllowedHost = (host) => trustedDomains.some((domain) => host === domain || host.endsWith(`.${domain}`));
    let parsedUrl;

    try {
      parsedUrl = new URL(rawWebsite);
    } catch (error) {
      console.warn('Ignored invalid franchise redirect URL', rawWebsite, error);
      return null;
    }

    if (parsedUrl.protocol !== 'https:' || !isAllowedHost(parsedUrl.hostname)) {
      console.warn('Blocked untrusted franchise redirect URL', parsedUrl.toString());
      return null;
    }

    parsedUrl.searchParams.set('postal_code', normalizedPostalCode);
    return parsedUrl.toString();
  }

  onKeyDown(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.close();
    }
  }

  onCloseButtonClick() {
    this.close();
  }

  onDocumentClick(event) {
    if (!this.isDialogOpen) {
      return;
    }

    const clickInsideDialog = this.dialog.contains(event.target);
    const clickOnToggle = this.toggleButton.contains(event.target);

    if (!clickInsideDialog && !clickOnToggle) {
      this.close(false);
    }
  }

  onResize() {
    const nextIsMobile = window.innerWidth <= this.mobileBreakpoint;

    if (nextIsMobile !== this.isMobileLayout) {
      this.close(false);
      this.updateResponsiveLayout();
      return;
    }

    if (this.isDialogOpen && this.isMobileLayout) {
      this.setMobileOverlayOffset();
    }
  }

  updateResponsiveLayout() {
    const shouldUseMobileLayout = window.innerWidth <= this.mobileBreakpoint;

    if (shouldUseMobileLayout === this.isMobileLayout) {
      return;
    }

    this.isMobileLayout = shouldUseMobileLayout;

    if (!this.headerWrapper || !this.headerElement) {
      return;
    }

    if (shouldUseMobileLayout) {
      if (this.parentElement !== this.headerWrapper) {
        this.headerWrapper.insertBefore(this, this.headerElement.nextSibling);
      }
      return;
    }

    if (this.originalParent && this.parentElement !== this.originalParent) {
      try {
        this.originalParent.insertBefore(this, this.originalNextSibling);
      } catch (e) {
        this.originalParent.appendChild(this);
      }
    }
  }

  setMobileOverlayOffset() {
    const badgeTop = this.getBoundingClientRect().top;
    this.style.setProperty('--postal-badge-overlay-top', `${Math.max(badgeTop, 0)}px`);
  }

  open() {
    this.isDialogOpen = true;
    this.dialog.hidden = false;
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.action.textContent = this.action.dataset.closeText || this.action.textContent;
    this.input.value = this.currentPostalCode;

    this.overlay = document.createElement('div');
    this.overlay.className = 'postal-code-badge__overlay';
    this.overlay.addEventListener('click', () => this.close(false));
    this.appendChild(this.overlay);

    if (this.isMobileLayout) {
      this.setMobileOverlayOffset();
    }

    // Delay binding to avoid closing immediately from the opening click event.
    window.setTimeout(() => {
      document.addEventListener('click', this.onDocumentClick);
    }, 0);

    document.addEventListener('keydown', this.onKeyDown);
    document.body.classList.add('overflow-hidden');

    if (!this.isMobileLayout) {
      trapFocus(this.dialog, this.input);
    }

    this.input.focus();
    this.input.select();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'postal_code_badge_opened'
    });
  }

  close(restoreFocus = true) {
    if (!this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = false;
    this.dialog.hidden = true;
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.action.textContent = this.action.dataset.openText || this.action.textContent;
    this.style.removeProperty('--postal-badge-overlay-top');

    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
    document.body.classList.remove('overflow-hidden');

    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }

    if (!this.isMobileLayout) {
      removeTrapFocus(restoreFocus ? this.toggleButton : null);
    } else if (restoreFocus) {
      this.toggleButton.focus();
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'postal_code_badge_closed'
    });
  }
}

customElements.define('postal-code-badge', PostalCodeBadge);
